<template>
  <div class="odin-demo">
    <div class="demo-topbar">
      <div>
        <span class="live-dot"></span><strong>Workflow studio</strong
        ><span class="demo-version"> / ODIN preview</span>
      </div>
      <span class="sample-pill">SAMPLE DATA</span>
    </div>
    <p class="demo-disclosure">
      Interactive demo — sample data, no accounts connected
    </p>
    <div class="demo-toolbar">
      <label
        >Example
        <select
          aria-label="Sample scenario"
          v-model="kind"
          :disabled="running"
          @change="reset"
        >
          <option value="decline">Traffic watch</option>
          <option value="recovery">Recovery check</option>
          <option value="growth">Conversion growth</option>
          <option value="errors">Error increase</option>
          <option value="latency">Slower responses</option>
          <option value="ranking">Ranking improvement</option>
        </select></label
      >
      <div class="demo-actions">
        <button :disabled="running" @click="arrange">Arrange blocks</button
        ><button :disabled="running" @click="reset">Reset</button
        ><button class="run-button" :disabled="running" @click="run">
          {{ running ? "Running…" : "▶ Run demo" }}
        </button>
      </div>
    </div>
    <div v-if="logs.length && !running" class="run-summary" role="status">
      <span>{{ reactionLabel }} · {{ notice }}</span>
      <button @click="showResult">View result ↓</button>
    </div>
    <div class="demo-audio">
      <div>
        <button
          type="button"
          :aria-pressed="String(soundEnabled)"
          :disabled="soundBusy"
          @click="toggleSound"
        >
          {{ soundEnabled ? "Mute sound" : "Enable sound" }}
        </button>
        <button
          type="button"
          :disabled="!soundEnabled || quietPlan || running"
          @click="previewSound"
        >
          Preview sound
        </button>
      </div>
      <span role="status">{{
        quietPlan ? "Quiet mode · no sound" : soundStatus
      }}</span>
      <small>Optional robot sound cues · off by default</small>
    </div>
    <div class="studio-body">
      <div class="canvas-column">
        <div class="editor-views" role="group" aria-label="Workflow view">
          <button
            :aria-pressed="editorView === 'list'"
            @click="editorView = 'list'"
          >
            List view
          </button>
          <button
            :aria-pressed="editorView === 'canvas'"
            @click="editorView = 'canvas'"
          >
            Canvas view
          </button>
        </div>
        <div class="palette">
          <span>ADD A BLOCK</span
          ><button
            v-for="type in ['site', 'task', 'notify']"
            :key="type"
            :disabled="running || nodes.length >= 10"
            @click="add(type)"
          >
            + {{ types[type].label }}
          </button>
        </div>
        <p v-if="editorView === 'canvas'" id="canvas-hint" class="canvas-hint">
          Drag to move · select to edit · output, then input to connect. All
          actions also work in the inspector. Scroll inside the canvas to see
          the full route.
        </p>
        <div v-if="editorView === 'list'" class="workflow-list">
          <p>
            Choose a block, then edit its settings below. Each output shows the
            next connected step.
          </p>
          <ol>
            <li v-for="(node, index) in nodes" :key="node.id">
              <button
                :disabled="running"
                :aria-pressed="selected === node.id"
                @click="select(node.id)"
              >
                <strong>{{ index + 1 }}. {{ types[node.type].label }}</strong>
                <span>{{ description(node) }}</span>
                <small>{{ nextStep(node.id) }} · {{ statusLabel(node) }}</small>
              </button>
            </li>
          </ol>
        </div>
        <div
          v-show="editorView === 'canvas'"
          class="canvas-scroll"
          tabindex="0"
          role="region"
          aria-describedby="canvas-hint"
          aria-label="Workflow canvas, horizontally scrollable on small screens"
        >
          <div class="workflow-canvas" :style="{ height: canvasHeight + 'px' }">
            <svg
              class="connections"
              width="800"
              :height="canvasHeight"
              aria-hidden="true"
            >
              <path
                v-for="edge in edges"
                :key="edge.from + edge.to"
                :d="edgePath(edge)"
                :class="{
                  traversed: statuses[edge.from] === 'complete',
                  active: statuses[edge.to] === 'running',
                }"
              />
            </svg>
            <div
              v-for="(node, index) in nodes"
              :key="node.id"
              class="workflow-node"
              :class="[
                node.type,
                {
                  reverse: Math.floor(index / 3) % 2 === 1,
                  selected: selected === node.id,
                  running: statuses[node.id] === 'running',
                  done: statuses[node.id] === 'complete',
                  skipped: statuses[node.id] === 'skipped',
                },
              ]"
              :style="{ left: node.x + 'px', top: node.y + 'px' }"
            >
              <button
                v-if="node.type !== 'schedule'"
                class="node-handle input"
                :disabled="running"
                :aria-label="
                  'Connect input of ' +
                  types[node.type].label +
                  ' ' +
                  (index + 1)
                "
                @click="connectInput(node.id)"
              ></button>
              <button
                class="node-content"
                :disabled="running"
                :aria-pressed="selected === node.id"
                @click="select(node.id)"
                @pointerdown="startDrag($event, node)"
                @keydown.alt.up.prevent="move(node, 0, -24)"
                @keydown.alt.down.prevent="move(node, 0, 24)"
                @keydown.alt.left.prevent="move(node, -24, 0)"
                @keydown.alt.right.prevent="move(node, 24, 0)"
              >
                <span class="node-heading"
                  ><span class="node-icon">{{ types[node.type].icon }}</span
                  ><strong>{{ types[node.type].label }}</strong
                  ><span class="node-order">{{ index + 1 }}</span></span
                ><span class="node-description">{{ description(node) }}</span
                ><span class="node-state">{{ statusLabel(node) }}</span>
              </button>
              <button
                class="node-handle output"
                :disabled="running"
                :aria-label="
                  'Use output of ' + types[node.type].label + ' ' + (index + 1)
                "
                :aria-pressed="pending === node.id"
                @click="
                  pending = node.id;
                  notice =
                    'Choose a compatible input handle, or use the inspector connection control.';
                "
              ></button>
            </div>
          </div>
        </div>
      </div>
      <aside class="node-inspector" aria-label="Selected block settings">
        <div class="inspector-title">BLOCK INSPECTOR <span>↗</span></div>
        <label for="selected-block">Selected block</label
        ><select
          id="selected-block"
          v-model="selected"
          :disabled="running"
          @change="target = ''"
        >
          <option
            v-for="(node, index) in nodes"
            :key="node.id"
            :value="node.id"
          >
            {{ index + 1 }}. {{ types[node.type].label }}
          </option>
        </select>
        <template v-if="current">
          <div class="inspector-description">
            {{
              current.type === "condition"
                ? "Compare the sample KPI change against your threshold."
                : types[current.type].description
            }}
          </div>
          <label v-if="current.type === 'schedule'"
            >Weekday run time<input
              type="time"
              v-model="current.time"
              :disabled="running"
          /></label>
          <template v-if="current.type === 'condition'"
            ><label for="threshold"
              >{{
                comparison.direction === "increase" ? "Increase" : "Decrease"
              }}
              threshold (%)</label
            >
            <div class="threshold-field">
              <input
                id="threshold"
                type="number"
                min="1"
                max="100"
                v-model.number="current.threshold"
                :disabled="running"
              /><span>%</span>
            </div></template
          >
          <div
            v-if="['condition', 'source'].includes(current.type)"
            class="fixture"
          >
            <span>SAMPLE {{ sample.metric.toUpperCase() }}</span>
            <div>
              <b
                >{{ sample.previous.toLocaleString("en") }} →
                {{ sample.current.toLocaleString("en")
                }}{{ sample.unit ? " " + sample.unit : "" }}</b
              >
            </div>
            <p>
              {{ comparison.change }}% {{ comparison.direction }} ·
              {{ sample.higherIsBetter ? "Higher" : "Lower" }} is better.
            </p>
            <small
              >Absolute change ÷ previous value × 100. Latest complete 7 days vs
              previous 7 days. Fictional data.</small
            >
          </div>
          <label v-if="current.type === 'notify'"
            >Notification mode<select
              v-model="current.mode"
              :disabled="running"
            >
              <option value="desk">Desk update (demo)</option>
              <option value="workspace">Workspace update</option>
              <option value="quiet">Quiet — log only</option>
            </select></label
          >
          <label for="connect-target">Connect output to</label
          ><select id="connect-target" v-model="target" :disabled="running">
            <option value="">Choose a block</option>
            <option v-for="node in targets" :key="node.id" :value="node.id">
              {{ nodes.indexOf(node) + 1 }}. {{ types[node.type].label }}
            </option>
          </select>
          <div class="inspector-buttons">
            <button
              :disabled="!target || running"
              @click="connect(current.id, target)"
            >
              Connect</button
            ><button
              :disabled="running || !edges.some((e) => e.from === current.id)"
              @click="disconnect"
            >
              Disconnect output
            </button>
          </div>
          <span class="inspector-label">POSITION · OR ALT + ARROW KEYS</span>
          <div class="move-buttons">
            <button
              v-for="direction in directions"
              :key="direction.label"
              :aria-label="'Move selected block ' + direction.label"
              :disabled="running"
              @click="move(current, direction.x, direction.y)"
            >
              {{ direction.icon }}
            </button>
          </div>
          <button
            v-if="current.added || current.type === 'notify'"
            class="delete-block"
            :disabled="running"
            @click="remove"
          >
            Remove block
          </button>
        </template>
      </aside>
    </div>
    <div class="demo-feedback" role="status" aria-live="polite">
      {{
        notice ||
        "Ready when you are. Try the 20% threshold, then change it to 30%."
      }}
    </div>
    <div
      id="odin-demo-results"
      ref="results"
      class="demo-results"
      tabindex="-1"
    >
      <div class="event-log">
        <div class="log-title">
          ACTIVITY LOG
          <span>{{
            logs.length ? logs.length + " events" : "Waiting for a run"
          }}</span>
        </div>
        <ol v-if="logs.length">
          <li v-for="(log, i) in logs" :key="i">
            <span>{{ String(i + 1).padStart(2, "0") }}</span
            >{{ log }}
          </li>
        </ol>
        <p v-else>
          Your workflow’s steps will appear here. Nothing leaves this browser.
        </p>
      </div>
      <div class="desk-update" :class="'state-' + robotState">
        <div class="robot-reaction" :data-state="robotState">
          <picture
            ><source :srcset="reactionImage + '.webp'" type="image/webp" />
            <img
              :src="reactionImage + '.png'"
              width="320"
              height="320"
              loading="lazy"
              :alt="'ODIN concept reaction: ' + reactionLabel"
          /></picture>
          <span class="reaction-indicator" aria-hidden="true"></span>
        </div>
        <span class="inspector-label"
          >{{ reactionLabel.toUpperCase() }} · DEMO</span
        >
        <p aria-live="polite">
          {{
            notification ||
            "A little presence. A clear update. Run the example to meet ODIN."
          }}
        </p>
        <small
          >Optional robot interaction · rendered concept reactions. No microphone.
          Sample data only.</small
        >
      </div>
    </div>
  </div>
</template>
<script>
const {
  types,
  preset,
  canConnect,
  simulate,
  scenarios,
  assessMetric,
  validate,
} = require("./workflow");
const { createSoundEngine } = require("./sound");
export default {
  name: "OdinWorkflow",
  data() {
    const graph = preset();
    return {
      ...graph,
      types,
      selected: "n3",
      target: "",
      pending: "",
      running: false,
      statuses: {},
      logs: [],
      notice: "",
      notification: "",
      robotState: "idle",
      editorView: window.matchMedia("(max-width: 900px)").matches
        ? "list"
        : "canvas",
      soundEnabled: false,
      soundBusy: false,
      soundStatus: "Sound off",
      soundEngine: null,
      drag: null,
      timers: [],
      directions: [
        { label: "left", x: -24, y: 0, icon: "←" },
        { label: "up", x: 0, y: -24, icon: "↑" },
        { label: "down", x: 0, y: 24, icon: "↓" },
        { label: "right", x: 24, y: 0, icon: "→" },
      ],
    };
  },
  computed: {
    sample() {
      return scenarios[this.kind] || scenarios.decline;
    },
    comparison() {
      return assessMetric(
        this.sample,
        this.nodes.find((n) => n.type === "condition").threshold,
      );
    },
    quietPlan() {
      const result = validate({ nodes: this.nodes, edges: this.edges });
      return !!(
        result.route &&
        result.route.some((n) => n.type === "notify" && n.mode === "quiet")
      );
    },
    reactionImage() {
      const name = ["positive", "attention", "recovery"].includes(
        this.robotState,
      )
        ? "reaction-" + this.robotState
        : "cutout";
      return "/odin/v2/" + name;
    },
    reactionLabel() {
      return {
        idle: "Calm · no alert",
        checking: "Checking",
        positive: "Positive result",
        attention: "Attention needed",
        recovery: "Recovery confirmed",
        quiet: "Quiet mode",
      }[this.robotState];
    },
    current() {
      return this.nodes.find((n) => n.id === this.selected);
    },
    targets() {
      return this.nodes.filter((n) =>
        canConnect(this.nodes, this.selected, n.id),
      );
    },
    canvasHeight() {
      return Math.max(400, ...this.nodes.map((n) => n.y + 150));
    },
  },
  mounted() {
    this.soundEngine = createSoundEngine();
    document.addEventListener("visibilitychange", this.stopHiddenSound);
  },
  watch: {
    quietPlan(value) {
      if (value) {
        if (this.soundEngine) this.soundEngine.stop();
        this.robotState = "quiet";
      } else if (this.robotState === "quiet") this.robotState = "idle";
    },
  },
  beforeDestroy() {
    if (this.soundEngine) this.soundEngine.dispose();
    document.removeEventListener("visibilitychange", this.stopHiddenSound);
    this.timers.forEach((t) => clearTimeout(t));
    window.removeEventListener("pointermove", this.dragMove);
    window.removeEventListener("pointerup", this.endDrag);
  },
  methods: {
    nextStep(id) {
      const edge = this.edges.find((e) => e.from === id);
      const next = edge && this.nodes.find((n) => n.id === edge.to);
      return next ? "Next → " + types[next.type].label : "No connected output";
    },
    showResult() {
      this.$refs.results.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
      this.$refs.results.focus({ preventScroll: true });
    },
    async toggleSound() {
      if (this.soundEnabled) {
        this.soundEngine.mute();
        this.soundEnabled = false;
        this.soundStatus = "Sound off";
        return;
      }
      this.soundBusy = true;
      try {
        this.soundEnabled = await this.soundEngine.enable();
        this.soundStatus = this.soundEnabled
          ? "Sound enabled · quiet cues"
          : "Sound off";
      } catch (_) {
        this.soundStatus = "Sound unavailable · visual feedback still works";
      } finally {
        this.soundBusy = false;
      }
    },
    playCue(name) {
      if (!this.quietPlan && this.soundEnabled && this.soundEngine.play(name))
        this.soundStatus =
          "Last cue: " +
          {
            checking: "checking",
            positive: "positive result",
            attention: "attention needed",
            recovery: "recovery confirmed",
          }[name];
    },
    previewSound() {
      this.playCue("positive");
    },
    stopHiddenSound() {
      if (document.hidden && this.soundEngine) this.soundEngine.stop();
    },
    description(node) {
      if (node.type === "condition")
        return `${this.sample.metric} ${this.comparison.direction} > ${node.threshold}%`;
      if (node.type === "schedule") return `Weekdays at ${node.time}`;
      if (node.type === "notify")
        return node.mode === "quiet"
          ? "Quiet mode · log only"
          : `${node.mode === "desk" ? "Desk" : "Workspace"} update · text`;
      return types[node.type].description;
    },
    statusLabel(node) {
      return (
        { running: "Checking…", complete: "✓ Complete", skipped: "— Skipped" }[
          this.statuses[node.id]
        ] || (node.added ? "Added block" : "Sample block")
      );
    },
    select(id) {
      this.selected = id;
      this.target = "";
    },
    reset() {
      if (this.soundEngine) this.soundEngine.stop();
      const graph = preset(this.kind);
      this.nodes = graph.nodes;
      this.edges = graph.edges;
      this.selected = "n3";
      this.target = "";
      this.pending = "";
      this.statuses = {};
      this.logs = [];
      this.notification = "";
      this.notice = "Example reset. All data is fictional.";
      this.robotState = "idle";
    },
    arrange() {
      this.nodes.forEach((n, i) => {
        const row = Math.floor(i / 3);
        n.x = 24 + (row % 2 ? 2 - (i % 3) : i % 3) * 262;
        n.y = 40 + row * 178;
      });
      this.notice = "Blocks arranged. Connections are unchanged.";
    },
    add(type) {
      if (this.running || this.nodes.length >= 10) return;
      const id = "added-" + Date.now();
      this.nodes.push({
        id,
        type,
        x: 24,
        y: this.canvasHeight - 10,
        mode: "desk",
        threshold: 20,
        added: true,
      });
      this.select(id);
      this.notice = `${types[type].label} added. Connect it to the route using the inspector.`;
    },
    connect(from, to) {
      if (!canConnect(this.nodes, from, to)) {
        this.notice =
          "These blocks cannot connect. Choose a compatible block in the inspector.";
        return;
      }
      const candidate = this.edges
        .filter((e) => e.from !== from && e.to !== to)
        .concat({ from, to });
      let id = to;
      const seen = new Set([from]);
      while (id) {
        if (seen.has(id)) {
          this.notice = "Loops are not supported. Choose a later block.";
          return;
        }
        seen.add(id);
        const edge = candidate.find((e) => e.from === id);
        id = edge && edge.to;
      }
      this.edges = candidate;
      this.pending = "";
      this.target = "";
      this.statuses = {};
      this.notification = "";
      this.notice =
        "Connection saved. A new connection replaces the existing output and destination input.";
    },
    connectInput(id) {
      if (!this.pending) {
        this.notice = "Choose an output handle first, then this input.";
        return;
      }
      this.connect(this.pending, id);
    },
    disconnect() {
      this.edges = this.edges.filter((e) => e.from !== this.selected);
      this.statuses = {};
      this.notification = "";
      this.notice =
        "Output disconnected. Only blocks on the connected route can run.";
    },
    remove() {
      const id = this.selected;
      this.nodes = this.nodes.filter((n) => n.id !== id);
      this.edges = this.edges.filter((e) => e.from !== id && e.to !== id);
      this.select("n3");
      this.notification = "";
      this.statuses = {};
      this.notice =
        "Block removed. Reconnect any interrupted path before running.";
    },
    move(node, x, y) {
      if (this.running) return;
      node.x = Math.max(16, Math.min(564, node.x + x));
      node.y = Math.max(24, Math.min(1000, node.y + y));
    },
    startDrag(event, node) {
      if (this.running || event.pointerType === "touch" || event.button !== 0)
        return;
      this.select(node.id);
      this.drag = { node, x: event.clientX, y: event.clientY };
      window.addEventListener("pointermove", this.dragMove);
      window.addEventListener("pointerup", this.endDrag);
    },
    dragMove(e) {
      if (!this.drag) return;
      this.move(
        this.drag.node,
        e.clientX - this.drag.x,
        e.clientY - this.drag.y,
      );
      this.drag.x = e.clientX;
      this.drag.y = e.clientY;
    },
    endDrag() {
      this.drag = null;
      window.removeEventListener("pointermove", this.dragMove);
      window.removeEventListener("pointerup", this.endDrag);
    },
    edgePath(edge) {
      const a = this.nodes.find((n) => n.id === edge.from),
        b = this.nodes.find((n) => n.id === edge.to);
      if (!a || !b) return "";
      const reverseA = Math.floor(this.nodes.indexOf(a) / 3) % 2 === 1,
        reverseB = Math.floor(this.nodes.indexOf(b) / 3) % 2 === 1;
      const x = a.x + (reverseA ? 0 : 216),
        y = a.y + 54,
        X = b.x + (reverseB ? 216 : 0),
        Y = b.y + 54;
      return `M ${x} ${y} C ${x + (reverseA ? -46 : 46)} ${y}, ${X + (reverseB ? 46 : -46)} ${Y}, ${X} ${Y}`;
    },
    pause(ms) {
      return new Promise((resolve) =>
        this.timers.push(setTimeout(resolve, ms)),
      );
    },
    async run() {
      if (this.running) return;
      this.notification = "";
      this.logs = [];
      this.statuses = {};
      const result = simulate({
        kind: this.kind,
        nodes: this.nodes,
        edges: this.edges,
      });
      if (result.error) {
        this.notice = result.error;
        this.robotState = this.quietPlan ? "quiet" : "idle";
        return;
      }
      this.running = true;
      this.robotState = result.quiet ? "quiet" : "checking";
      if (!result.quiet) this.playCue("checking");
      this.notice = "Running the connected route on fictional data…";
      const delay = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? 0
        : 420;
      for (const step of result.steps) {
        this.$set(this.statuses, step.id, "running");
        await this.pause(delay);
        this.$set(this.statuses, step.id, step.status);
        this.logs.push(step.message);
        if (step.notification) this.notification = step.notification;
      }
      if (result.detached.length)
        this.logs.push(
          `${result.detached.length} disconnected block(s) did not run.`,
        );
      this.running = false;
      this.robotState = result.reaction;
      if (result.hasNotification && !result.quiet)
        this.playCue(result.reaction);
      this.notice = result.branch
        ? "Demo complete. Review the sample activity below."
        : "Demo complete. Threshold not exceeded; the update branch was skipped.";
      if (!result.hasNotification)
        this.notification =
          "No notification sent. The workflow respected its connections, threshold and quiet-mode settings.";
    },
  },
};
</script>
